import React from 'react';
import styled from 'styled-components';

import { getBucketVariantColors } from '@/features/csvImport/admin/constants/bucketVariants';
import { CsvImportBucketVariant } from '@/features/csvImport/admin/types/csvImport';
import { Icon, IconPropType } from '@/ui/Icon';

const CARD_MIN_HEIGHT = 112;
const COUNT_ROW_HEIGHT = 38;
const COUNT_ROW_BOTTOM_SPACE = 5;
const ICON_BOX_SIZE = 24;

const RootStyled = styled.div<{ $variant: CsvImportBucketVariant }>`
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: ${CARD_MIN_HEIGHT}px;
  padding: 13px 15px;
  border: 1px solid ${(p) => getBucketVariantColors(p.theme, p.$variant).accent};
  border-radius: 8px;
  background-color: ${(p) => getBucketVariantColors(p.theme, p.$variant).surface};
`;

/**
 * The design separates the count from the label with a fixed-height row rather than a gap on
 * the card, which leaves the icon and count centred in the space above the bottom padding.
 */
const CountRowStyled = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 7px;
  height: ${COUNT_ROW_HEIGHT}px;
  padding-bottom: ${COUNT_ROW_BOTTOM_SPACE}px;
`;

/** The glyph is half the size of the box the design reserves for it, so the box is explicit. */
const IconBoxStyled = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${ICON_BOX_SIZE}px;
  height: ${ICON_BOX_SIZE}px;
`;

const IconStyled = styled(Icon)<{ $variant: CsvImportBucketVariant }>`
  ${(p) => p.theme.typography.icon2};
  color: ${(p) => getBucketVariantColors(p.theme, p.$variant).accent};
`;

const CountStyled = styled.span`
  ${(p) => p.theme.typography.title3};
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const LabelStyled = styled.span<{ $variant: CsvImportBucketVariant }>`
  ${(p) => p.theme.typography.overline};
  color: ${(p) => getBucketVariantColors(p.theme, p.$variant).label};
`;

type CsvImportSummaryCardProps = {
  icon: IconPropType;
  count: number;
  label: string;
  variant?: CsvImportBucketVariant;
  className?: string;
};

export const CsvImportSummaryCard = ({
  icon,
  count,
  label,
  variant = 'neutral',
  className,
}: CsvImportSummaryCardProps): JSX.Element => (
  <RootStyled className={className} $variant={variant}>
    <CountRowStyled>
      <IconBoxStyled>
        <IconStyled icon={icon} $variant={variant} aria-hidden />
      </IconBoxStyled>
      <CountStyled>{count}</CountStyled>
    </CountRowStyled>
    <LabelStyled $variant={variant}>{label}</LabelStyled>
  </RootStyled>
);
